import React, { useEffect, useState } from 'react';
import { CFormLabel } from '@coreui/react';
import { useAuth } from '../contexts/AuthContext';
import { useReport } from '../contexts/ReportContext';
import { reportNav } from '../components/NavigationPath';
import { formatDateToBackend, openFileWithLoading } from '../components/Helper';
import Layout2 from '../components/Layout2';
import PageHeader from '../components/PageHeader';
import DateRangeSelection from '../components/DateRangeSelection';
import HorizontalBarChart from '../components/HorizontalBarChart';

function CategoryReport() {
    const { categoryReportCharts, categoryReportPDF, categoryReportCSV } = useReport();
    const { restaurantId } = useAuth();

    const currentDate = new Date();
    const yesterdayDate = new Date(currentDate);
    yesterdayDate.setDate(currentDate.getDate() - 1);

    const [dates, setDates] = useState([yesterdayDate, currentDate]);
    const [isLoading, setIsLoading] = useState(false);
    const [categoryRevenue, setCategoryRevenue] = useState({
        x: [],
        y: []
    });

    const startDate = dates.length > 0 && dates[0] ? formatDateToBackend(dates[0]) : null;
    const endDate = dates.length > 0 && dates[1] ? formatDateToBackend(dates[1]) : null;

    useEffect(() => {
        if (restaurantId && startDate && endDate) {
            fetchCategoryReportCharts();
        }
    }, [restaurantId, startDate, endDate]);

    const fetchCategoryReportCharts = async () => {
        setIsLoading(true);
        try {
            const reports = await categoryReportCharts(startDate, endDate);
            if (reports.categoryReport.length > 0) {
                const categories = reports.categoryReport.map(item => item.categoryName);
                const revenues = reports.categoryReport.map(item => parseFloat(item.revenue));

                setCategoryRevenue({
                    x: categories,
                    y: revenues
                });
            }
        } catch (error) {
            console.error('Error fetching category report charts', error);
        } finally {
            setIsLoading(false);
        }
    };

    const generatePDF = async (pdfType) => {
        openFileWithLoading({
            apiCall: categoryReportPDF,
            apiParams: [startDate, endDate, pdfType],
            fileType: 'pdf',
            loadingMessage: `Generating ${pdfType === 'summary' ? 'Summary' : 'Detailed'} PDF...`,
            errorMessage: `Failed to generate ${pdfType === 'summary' ? 'Summary' : 'Detailed'} PDF`,
        });
    }

    const generateCSV = async (csvType) => {
        openFileWithLoading({
            apiCall: categoryReportCSV,
            apiParams: [startDate, endDate, csvType],
            fileType: 'csv',
            loadingMessage: `Generating ${csvType === 'summary' ? 'Summary' : 'Detailed'} CSV...`,
            errorMessage: `Failed to generate ${csvType === 'summary' ? 'Summary' : 'Detailed'} CSV`,
        });
    }

    return (
        <Layout2 items={reportNav.items} navTitle={reportNav.header}>
            <div className="d-flex flex-column w-100">
                <PageHeader title="Category Reports" />
                <div className="d-flex flex-row px-4 py-2 user-list-nav justify-content-between">
                    <p className='pt-2'>
                        Print or download the category reports: <span className='view_button' onClick={() => generatePDF('summary')}>PDF report</span> / <span className='view_button' onClick={() => generateCSV('summary')}>CSV</span>
                    </p>
                    <DateRangeSelection
                        dates={dates}
                        setDates={setDates} />
                </div>
                <div className="px-4 py-2">
                    {
                        categoryRevenue.x.length > 0 && categoryRevenue.y.length > 0 && (
                            <div className='w-100 text-center mt-5'>
                                <CFormLabel className='mb-3'>Product Category Revenues</CFormLabel>
                                <HorizontalBarChart
                                    x={categoryRevenue.x}
                                    y={categoryRevenue.y}
                                    yLabel="Revenue"
                                />
                            </div>
                        )
                    }
                </div>
            </div>
        </Layout2>
    );
}

export default CategoryReport;
